import type {
  PayrollStatementNotificationRecipient,
  PayrollStatementPackApproval,
  PayrollStatementPackApprovalsResponse,
} from "@/payroll/pay-breakdown/payroll-statement-notifications/types";

export const canApprovePack = (
  recipient: PayrollStatementNotificationRecipient,
) => recipient.eligible || !!recipient.pack_released;

const keepKnownComparison = (
  known: PayrollStatementPackApproval | null | undefined,
  polled: PayrollStatementPackApproval | null,
) => {
  if (!polled || polled.current !== null) return polled;
  if (!known || known.current === null) return polled;
  if (
    known.approved_at !== polled.approved_at ||
    known.ai_script_run_id !== polled.ai_script_run_id
  ) {
    return polled;
  }

  return { ...polled, current: known.current };
};

export const mergePolledPackApprovals = (
  recipients: PayrollStatementNotificationRecipient[],
  polled: PayrollStatementPackApprovalsResponse | undefined,
  known: Map<string, PayrollStatementPackApproval> = new Map(),
): PayrollStatementNotificationRecipient[] => {
  if (!polled) return recipients;

  const approvalByTechnician = new Map(
    polled.pack_approvals.map(({ technician_id, ...approval }) => [
      technician_id,
      approval,
    ]),
  );
  const historyByTechnician = new Map(
    (polled.approval_histories ?? []).map(({ technician_id, history }) => [
      technician_id,
      history,
    ]),
  );
  const released = new Set(polled.released_technician_ids);

  return recipients.map((recipient) => ({
    ...recipient,
    pack_released: released.has(recipient.technician_id),
    pack_approval: keepKnownComparison(
      known.get(recipient.technician_id) ?? recipient.pack_approval,
      approvalByTechnician.get(recipient.technician_id) ?? null,
    ),
    approval_history: polled.approval_histories
      ? (historyByTechnician.get(recipient.technician_id) ?? [])
      : recipient.approval_history,
  }));
};

export const summarizePackApprovals = (
  recipients: PayrollStatementNotificationRecipient[],
) => {
  const countable = recipients.filter(canApprovePack);
  const approvals = countable
    .map((recipient) => recipient.pack_approval)
    .filter((approval) => !!approval);

  return {
    approved: approvals.filter((approval) => approval.current === true).length,
    stale: approvals.filter((approval) => approval.current === false).length,
    unknown: approvals.filter((approval) => approval.current === null).length,
    total: countable.length,
  };
};
